import { type SeverityLevel } from '@sentry/react'
import { intl } from 'lang/intl'

const fatalErrorMessages = {
  /** logged as fatal */

  FIREBASE_CONNECTION_ERROR: {
    log: 'Firebase connection failed',
    message: () =>
      intl.formatMessage({
        defaultMessage: "Failed to connect to the room's sync service.",
        id: '/CCaFi',
      }),
  },
  FETCH_SITE_DATA_ERROR: {
    log: 'Failed to load workspace data',
    message: () =>
      intl.formatMessage({
        defaultMessage: 'Failed to load configuration.',
        id: 'lALzsF',
      }),
  },
  LIVE_DATA_LOAD_ERROR: {
    log: 'Failed to load webinar data',
    message: () =>
      intl.formatMessage({
        defaultMessage: "Couldn't load data about the webinar. Try again?",
        id: '+omWCQ',
      }),
  },
  WEBRTC_PROVIDER_CONNECTION_FAILED: {
    log: "Couldn't connect to webRTC provider",
    message: () =>
      intl.formatMessage({
        defaultMessage: "Couldn't connect to the webinar",
        id: 'tobXPD',
      }),
  },
  WEBINAR_ROOM_CONNECTION_FAILED_TWILIO: {
    log: 'Failed to connect to twilio',
    message: () =>
      intl.formatMessage({
        defaultMessage: 'Failed to connect to webinar room.',
        id: 'xQOiK4',
      }),
  },
  WEBINAR_ROOM_CONNECTION_FAILED_LIVEKIT: {
    log: 'Failed to connect to livekit',
    message: () =>
      intl.formatMessage({
        defaultMessage: 'Failed to connect to webinar room.',
        id: 'xQOiK4',
      }),
  },
  RECONNECT_ERROR: {
    log: 'Sagas reconnect error.',
    message: () =>
      intl.formatMessage({
        defaultMessage:
          'We were unable to reconnect. Please contact our support at support@twentythree.com.',
        id: 'ScfMJo',
      }),
  },

  /** logged as info */

  PRESENTER_PRODUCER_MOBILE_DEVICE_NOT_SUPPORTED: {
    log: { level: 'info', message: 'Presenter joining from mobile device' },
    message: () =>
      intl.formatMessage({
        defaultMessage:
          'Joining as a presenter or producer from a mobile device is not supported. Please join from a laptop or a desktop device.',
        id: 'CYDeW+',
      }),
  },
  BROWSER_NOT_SUPPORTED: {
    log: { level: 'info', message: 'Loading room on unsupported browser' },
    message: () =>
      intl.formatMessage({
        defaultMessage:
          'Your browser is not supported. Please use either Chrome, Safari, Firefox or Edge.',
        id: 'ncC911',
      }),
  },
  BROWSER_WEBRTC_NOT_SUPPORTED: {
    log: { level: 'info', message: 'WebRTC not supported by browser' },
    message: () =>
      intl.formatMessage({
        defaultMessage:
          'Your browser does not support WebRTC technology that enables webinars. ' +
          'Please update your browser.',
        id: 'RhiJVk',
      }),
  },
  PRESENTER_TOOK_OVER_SESSION: {
    log: {
      level: 'info',
      message: 'Presenter already exists in room; Leaving',
    },
    message: () =>
      intl.formatMessage({
        defaultMessage:
          'Another presenter took over your session. Maybe you joined twice?',
        id: 'BIdZKl',
      }),
  },
  GEAR_MODE_REMOTE_SPEAKERS_UNSUPPORTED: {
    log: {
      level: 'info',
      message: 'Speaker attempting to join gear mode webinar',
    },
    message: () =>
      intl.formatMessage({
        defaultMessage:
          'This webinar is currently in Gear Mode, running from production gear and does not support joining remote speakers.',
        id: '7gGj7V',
      }),
  },
  WEBRTC_DISCONNECTED: {
    log: 'Maximum reconnection attempts reached. Participant is now disconnected.',
    message: () =>
      intl.formatMessage({
        defaultMessage: "You've been disconnected from the webinar.",
        id: 'iMtzJO',
      }),
  },

  /** not logged */

  WEBINAR_FINISHED: {
    log: undefined,
    message: () =>
      intl.formatMessage({
        defaultMessage: 'This webinar is finished',
        id: 'pbBapJ',
      }),
  },
} satisfies Record<
  string,
  {
    log: { level: SeverityLevel; message: string } | string | undefined
    message: () => string
  }
>

type FatalErrorKey = keyof typeof fatalErrorMessages

export { type FatalErrorKey, fatalErrorMessages }
